import { motion } from "motion/react"
import type { CSSProperties, ReactNode } from "react"

import { cn } from "@/lib/utils"

import { HighlighterStroke } from "./highlighter-stroke"
import { lineVariants } from "./motion"
import { findEmphasisPhrase, notesBlocks } from "./strip-html"
import { linerNotes } from "./type-voices"

/**
 * The curator's liner notes on a jacket face (§3.3 standfirst voice) —
 * block paragraphs under kickers and hairline rules, no book-style first-
 * line indent (mockup pass; the measure belongs to the column + disc
 * gutter). Blocks enter with the shared type stagger; `startIndex` slots
 * them after whatever blocks (title, byline) entered before them.
 *
 * `emphasis="stroke"` renders the curator's emphasized phrase under the
 * irregular highlighter — Candidate B's one yellow moment.
 */
export function JacketNotes({
  notes,
  reduce,
  startIndex = 0,
  emphasis = "none",
  className,
  style,
}: {
  notes: string
  reduce: boolean
  startIndex?: number
  emphasis?: "none" | "stroke"
  className?: string
  style?: CSSProperties
}) {
  const blocks = notesBlocks(notes)
  const phrase = emphasis === "stroke" ? findEmphasisPhrase(notes) : null
  // The stroke lands once: on the first block that contains the phrase.
  const strokeAt = phrase
    ? blocks.findIndex((block) => block.includes(phrase))
    : -1

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {blocks.map((block, i) => (
        <motion.p
          key={i}
          custom={startIndex + i}
          variants={lineVariants(reduce)}
          initial="hidden"
          animate="shown"
          style={{
            ...linerNotes,
            ...style,
          }}
        >
          {i === strokeAt && phrase ? withStroke(block, phrase) : block}
        </motion.p>
      ))}
    </div>
  )
}

function withStroke(block: string, phrase: string): ReactNode {
  const at = block.indexOf(phrase)
  return (
    <>
      {block.slice(0, at)}
      <HighlighterStroke>{phrase}</HighlighterStroke>
      {block.slice(at + phrase.length)}
    </>
  )
}
